import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminThemes } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';

const Themes = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const { search, searchInput, setSearchInput } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [scope, setScope] = useState(query.scope || '');
  const [deprecated, setDeprecated] = useState(query.deprecated || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(scope && { scope }),
        ...(deprecated && { deprecated }),
      }),
    });
  }, [sortBy, sortDirection, search, scope, deprecated]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    scope,
    deprecated,
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminThemes(filters);

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData = useMemo(() => data?.pages?.flatMap((page: any) => page.data) ?? [], [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Name" />,
        cell: ({ row }: any) => {
          const theme = row.original;
          const primaryColor = theme.primaryColor;
          return (
            <div className="flex items-center gap-2">
              {primaryColor && (
                <span
                  className="inline-block h-4 w-4 rounded-full border border-gray-200 dark:border-border shrink-0"
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              <span>{theme.name}</span>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'scope',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Scope" />,
        cell: ({ row }: any) => {
          const scope = row.getValue('scope');
          return (
            <Badge variant={scope === 'global' ? 'secondary' : 'outline'}>
              {scope === 'global' ? 'Global' : 'Seller'}
            </Badge>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Seller" />,
        cell: ({ row }: any) => {
          const theme = row.original;
          if (!theme.seller) {
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          }
          return (
            <Link href={`/admin/merchants/${theme.seller._id}`}>
              {theme.seller.displayName || theme.seller.name}
            </Link>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'usageCount',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Usage" />,
        cell: ({ row }: any) => {
          const count = row.getValue('usageCount');
          return (
            <span className={count === 0 ? 'text-zinc-400 dark:text-muted-foreground' : ''}>
              {count} {count === 1 ? 'page' : 'pages'}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'deprecated',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Deprecated" />,
        cell: ({ row }: any) => {
          const isDeprecated = row.getValue('deprecated');
          if (!isDeprecated) return null;
          return <Badge variant="outline">Deprecated</Badge>;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Date created" />,
        cell: ({ row }: any) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '';
        },
        enableSorting: true,
      },
    ],
    [],
  );

  const total = data?.pages[0]?.total;

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto text-sm text-gray-500 dark:text-muted-foreground">{total} themes</div>

      <div>
        <Label htmlFor="scope">Scope</Label>
        <Select
          name="scope"
          value={scope || 'all'}
          onValueChange={value => setScope(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="global">Global</SelectItem>
            <SelectItem value="seller">Seller</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="deprecated">Deprecated</Label>
        <Select
          name="deprecated"
          value={deprecated || 'all'}
          onValueChange={value => setDeprecated(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="!w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={(e: any) => setSearchInput(e.target.value)}
              placeholder="Search by theme name"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Themes</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No themes found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default Themes;
